import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { rescueRecordStalledAdopterEmailsMutation, type StalledReminderVariables } from '../../lib/rescueQueries';
import {
  STALLED_REMINDER_EMAIL_SUBJECT,
  distinctEmails,
  newReminderRequestId,
  stalledReminderEmailBody,
} from '../../lib/stalledReminders';
import useStalledAdopters from '../../hooks/useStalledAdopters';
import {
  type StalledEmailCopyField,
  trackStalledEmailAllClicked,
  trackStalledEmailAllConfirmed,
  trackStalledEmailAllCopied,
} from '../../analytics';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import NoticeIcon from '../../components/NoticeIcon/NoticeIcon';
import styles from './Devices.module.scss';
import r from './StalledReminders.module.scss';

const COPIED_TOAST: Record<Exclude<StalledEmailCopyField, 'emails'>, string> = {
  subject: 'Subject copied',
  message: 'Message copied',
};

interface CopyButtonProps {
  copied: boolean;
  label: string;
  onClick: () => void;
  /** Pins the button inside a text field's corner; the subject row lays it out inline instead. */
  overField?: boolean;
}

function CopyButton({ copied, label, onClick, overField = false }: CopyButtonProps) {
  return (
    <Button
      variant={copied ? 'greenLight' : 'ghost'}
      size="badge"
      className={overField ? r.copyButton : undefined}
      onClick={onClick}
      aria-label={label}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </Button>
  );
}

/**
 * Pasting the list into To would show every adopter everyone else's address, so copying it always
 * stops here until the rescue dismisses it. A takeover rather than a toast: the mistake it heads off
 * can't be undone once the email has gone.
 */
function BccReminder({ emails, onDismiss }: { emails: string[]; onDismiss: () => void }) {
  const sample = emails.slice(0, 3).join(', ') + (emails.length > 3 ? ', …' : '');
  return (
    <Modal open onClose={onDismiss} showCloseX={false} dismissOnBackdropClick={false}>
      <div className={r.bccCard}>
        <div className={r.bccIcon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2 className={r.bccTitle}>Paste these into BCC, not To</h2>
        <p className={r.bccText}>
          If you paste them into To, every adopter sees everyone else's email address. Put your own address in To. In
          Gmail, click <strong>Bcc</strong> at the right of the To line to show the field.
        </p>
        <div className={r.mockMail}>
          <div className={r.mockMailRow}>
            <span className={r.mockMailLabel}>To</span>
            <span className={r.mockMailValue}>Your own email address</span>
          </div>
          <div className={`${r.mockMailRow} ${r.mockMailRowBcc}`}>
            <span className={r.mockMailLabel}>Bcc</span>
            <span className={r.mockMailValue}>{sample}</span>
          </div>
        </div>
        <Button variant="green" onClick={onDismiss}>
          Got it
        </Button>
      </div>
    </Modal>
  );
}

interface EmailAllStalledModalProps {
  /** Signs the suggested email. */
  orgName: string;
  /** The modal mounts per opening, so every opening starts on the copy step with nothing copied. */
  onClose: () => void;
}

/**
 * Email all: Fi sends nothing. The rescue copies the addresses and a suggested message into its own
 * inbox, so replies reach the rescue, then tells the portal it sent them.
 */
export default function EmailAllStalledModal({ orgName, onClose }: EmailAllStalledModalProps) {
  const { showToast } = useToast();
  const { adopters, loading, error, refetch } = useStalledAdopters((loaded) => {
    trackStalledEmailAllClicked(loaded.filter((adopter) => adopter.emails.length > 0).length, loaded.length);
  });
  const [record, { loading: recording }] = useMutation<unknown, StalledReminderVariables>(
    rescueRecordStalledAdopterEmailsMutation,
    { refetchQueries: 'active' },
  );
  const [step, setStep] = useState<'copy' | 'confirm'>('copy');
  const [copied, setCopied] = useState<ReadonlySet<StalledEmailCopyField>>(new Set());
  const [bccReminderOpen, setBccReminderOpen] = useState(false);
  const [requestId] = useState(newReminderRequestId);

  const emailable = adopters.filter((adopter) => adopter.emails.length > 0);
  const emails = distinctEmails(adopters);
  const emailList = emails.join(', ');
  const body = stalledReminderEmailBody(orgName);
  const noEmailCount = adopters.length - emailable.length;

  function markCopied(field: StalledEmailCopyField) {
    setCopied((previous) => new Set(previous).add(field));
    trackStalledEmailAllCopied(field);
    if (field === 'emails') {
      setBccReminderOpen(true);
    }
  }

  async function copy(field: StalledEmailCopyField, text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      showToast("Couldn't copy automatically. Select the text and copy it instead.");
      return;
    }
    markCopied(field);
    if (field !== 'emails') {
      showToast(COPIED_TOAST[field]);
    }
  }

  // Logs every emailable adopter, as the rescue reports it. The portal can't see anyone dropped from the
  // list in the mail app, so these read as a self-reported batch, not a per-adopter delivery record.
  async function handleConfirm() {
    try {
      await record({
        variables: { input: { transferIds: emailable.flatMap((adopter) => adopter.transferIds), requestId } },
      });
      trackStalledEmailAllConfirmed(emailable.length, [...copied]);
      showToast(`Email reminder noted for ${emailable.length} ${emailable.length === 1 ? 'adopter' : 'adopters'}`);
      onClose();
    } catch (err) {
      showToast(`Couldn't record the emails: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  if (loading || error || emailable.length === 0) {
    let message = 'None of your stalled adopters has an email address on file.';
    if (loading) {
      message = 'Loading stalled adopters…';
    } else if (error) {
      message = "Couldn't load your stalled adopters.";
    } else if (adopters.length === 0) {
      message = 'No stalled adopters to email right now.';
    }
    return (
      <Modal open onClose={onClose} size="wide">
        <ModalTitle>Email stalled adopters</ModalTitle>
        <p className={r.statusMessage}>{message}</p>
        <ModalActions>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {error && (
            <Button variant="green" onClick={() => refetch()}>
              Try again
            </Button>
          )}
        </ModalActions>
      </Modal>
    );
  }

  if (step === 'confirm') {
    return (
      <Modal open onClose={recording ? () => {} : onClose} dismissOnBackdropClick={false}>
        <ModalTitle>Did you send the emails?</ModalTitle>
        <ModalSubtitle>Only confirm once the reminder has gone out.</ModalSubtitle>
        <div className={`${styles.notice} ${styles.noticeInfo}`}>
          <NoticeIcon variant="info" />
          <div>
            <strong>Fi doesn't send these emails.</strong> Send them from your own inbox, like Gmail or Outlook, so
            adopters can reply to you directly.
          </div>
        </div>
        <ModalActions>
          <Button variant="ghost" onClick={() => setStep('copy')} disabled={recording}>
            Not yet
          </Button>
          <Button variant="green" onClick={handleConfirm} disabled={recording}>
            {recording ? 'Saving…' : 'Yes, I sent them'}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <>
      <Modal open onClose={onClose} size="wide" dismissOnBackdropClick={false}>
        <ModalTitle>Email stalled adopters</ModalTitle>
        <ModalSubtitle>
          <strong>
            {emailable.length} of {adopters.length}
          </strong>{' '}
          reachable by email
          {noEmailCount > 0 && ` · ${noEmailCount} ${noEmailCount === 1 ? 'has' : 'have'} no email address on file`}.
          Send from your own inbox so adopters can reply to you directly.
        </ModalSubtitle>

        <div className={r.emailStep}>
          <span className={r.emailStepNumber}>1</span>
          <div className={r.emailStepBody}>
            <div className={r.emailStepTitle}>Copy the adopter emails</div>
            <p className={r.emailStepText}>
              In Gmail or your email app, put <strong>your own address in To</strong> and paste these into{' '}
              <strong>BCC</strong> so addresses stay private. Remove anyone you want to leave out.
            </p>
            <div className={r.copyField}>
              <textarea
                className={r.copyTextarea}
                rows={2}
                readOnly
                value={emailList}
                aria-label="Adopter emails"
                onCopy={() => markCopied('emails')}
              />
              <CopyButton
                copied={copied.has('emails')}
                label="Copy emails"
                onClick={() => copy('emails', emailList)}
                overField
              />
            </div>
          </div>
        </div>

        <div className={r.emailStep}>
          <span className={r.emailStepNumber}>2</span>
          <div className={r.emailStepBody}>
            <div className={r.emailStepTitle}>Copy the suggested message</div>
            <p className={r.emailStepText}>Change anything you like before sending.</p>
            <div className={r.subjectRow}>
              <span className={r.subjectLabel}>Subject</span>
              <span className={r.subjectValue}>{STALLED_REMINDER_EMAIL_SUBJECT}</span>
              <CopyButton
                copied={copied.has('subject')}
                label="Copy subject"
                onClick={() => copy('subject', STALLED_REMINDER_EMAIL_SUBJECT)}
              />
            </div>
            <div className={r.copyField}>
              <textarea
                className={r.copyTextarea}
                rows={4}
                readOnly
                value={body}
                aria-label="Suggested message"
                onCopy={() => markCopied('message')}
              />
              <CopyButton
                copied={copied.has('message')}
                label="Copy message"
                onClick={() => copy('message', body)}
                overField
              />
            </div>
          </div>
        </div>

        <div className={r.emailStep}>
          <span className={r.emailStepNumber}>3</span>
          <div className={r.emailStepBody}>
            <div className={r.emailStepTitle}>Send it and follow up</div>
            <p className={r.emailStepText}>
              As adopters reply, help them finish setup, or arrange to collect the collar if they are no longer
              interested in activating and using their Fi device.
            </p>
          </div>
        </div>

        <ModalActions>
          <Button variant="ghost" onClick={onClose}>
            Back
          </Button>
          <Button variant="green" onClick={() => setStep('confirm')}>
            I sent the emails
          </Button>
        </ModalActions>
      </Modal>
      {bccReminderOpen && <BccReminder emails={emails} onDismiss={() => setBccReminderOpen(false)} />}
    </>
  );
}
